import { useCallback, useMemo } from 'react';
import {
  FEATURE_CHANGE_FONT_FAMILY_ON_HOVER,
  FEATURE_FONT_FAMILY_DROPDOWN_RENDER_IN_FONT,
} from '../../../flags';
import { loadFontFromTextItem } from '../../../utils/text/load-font-from-text-item';
import { makeFontPreviewName } from '../../../utils/text/load-font-preview';
import { resolveFontFamilyCssStack } from '../../../utils/text/font-family-css-stack';
import { MenuItemSingleSelect } from '@crabs-ai/ds-react';
import { useTranslations } from 'next-intl';

interface FontFamilySelectionItemProps {
  fontFamily: string;
  isLoaded: boolean;
  start: number;
  size: number;
  isHighlighted: boolean;
  isSelected: boolean;
  applyFontFamily: (fontFamily: string) => void;
  setIsOpen: (isOpen: boolean) => void;
  previewedFont: React.RefObject<string | null>;
  resetFontFamily: () => void;
  previewFont: (fontFamilyToPreview: string) => Promise<void>;
}

export const FontFamilySelectionItem = ({
  fontFamily,
  isLoaded,
  start,
  size,
  isHighlighted,
  isSelected,
  applyFontFamily,
  setIsOpen,
  previewedFont,
  resetFontFamily,
  previewFont,
}: FontFamilySelectionItemProps) => {
  const t = useTranslations();
  const onClick = useCallback(() => {
    applyFontFamily(fontFamily);
    setIsOpen(false);
  }, [fontFamily, applyFontFamily, setIsOpen]);

  const onMouseEnter = useCallback(async () => {
    previewedFont.current = fontFamily;
    await loadFontFromTextItem({
      fontFamily,
      fontVariant: 'normal',
      fontWeight: '400',
      fontInfosDuringRendering: null,
    });
    if (previewedFont.current !== fontFamily) {
      return;
    }

    previewFont(fontFamily);
  }, [previewedFont, fontFamily, previewFont]);

  const onMouseLeave = useCallback(() => {
    previewedFont.current = null;
    resetFontFamily();
  }, [previewedFont, resetFontFamily]);

  const style = useMemo(() => {
    return {
      transform: `translateY(${start}px)`,
      height: `${size}px`,
      opacity: isLoaded ? 1 : 0,
    };
  }, [start, size, isLoaded]);

  return (
    <div
      className="absolute left-0 w-full"
      style={style}
      onMouseEnter={
        FEATURE_CHANGE_FONT_FAMILY_ON_HOVER ? onMouseEnter : undefined
      }
      onMouseLeave={
        FEATURE_CHANGE_FONT_FAMILY_ON_HOVER ? onMouseLeave : undefined
      }
      aria-label={fontFamily}
    >
      <MenuItemSingleSelect
        size="md"
        isSelected={isSelected}
        text={
          <span
            style={{
              fontFamily: FEATURE_FONT_FAMILY_DROPDOWN_RENDER_IN_FONT
                ? resolveFontFamilyCssStack(makeFontPreviewName(fontFamily))
                : 'inherit',
            }}
          >
            {t(`Fonts.${fontFamily}`)}
          </span>
        }
        onClick={onClick}
        className={
          isHighlighted
            ? '[&_.menu-item-single-select-text]:text-sys-cont-primary-default'
            : undefined
        }
      />
    </div>
  );
};
